import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  MenuIcon,
  ShipmentIcon,
  PickupsIcon,
  ExportDataIcon,
  TicketsIcon,
  AdvancedSearch,
  NewShipment,
  complaint,
  DataAnalysis,
} from "../../assets/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import logoImg from "../../assets/images/logo.png";

const sidebarStyles = {
  sidebar: {
    height: "calc(100vh - 70px)",
    backgroundColor: "#ffffff",
    color: "var(--primary-color)",
    fontWeight: 500,
    position: "sticky",
    top: 70,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    transition: "width 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
    zIndex: 100,
    borderInlineEnd: "1px solid #e2e8f0",
  },
  userSection: {
    display: "flex",
    alignItems: "center",
    padding: "24px 15px 20px",
    borderBottom: "1px solid #f1f5f9",
    gap: "12px",
  },
  avatar: {
    height: 42,
    borderRadius: "12px",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
  },
  avatarImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "12px",
  },
  avatarFallback: {
    fontSize: "18px",
    fontWeight: 700,
    color: "var(--primary-color)",
  },
  userName: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#1e293b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  nav: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "8px 0",
    scrollbarWidth: "none",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 15px",
    color: "#64748b",
    textDecoration: "none",
    whiteSpace: "nowrap",
    transition: "all 0.2s ease",
    cursor: "pointer",
    position: "relative",
    gap: "12px",
    fontSize: "14px",
    fontWeight: 500,
    borderRadius: "0",
    margin: "0",
  },
  navItemActive: {
    color: "var(--primary-color)",
    backgroundColor: "rgba(67, 95, 134, 0.08)",
    fontWeight: 600,
  },
  navItemHover: {
    color: "var(--primary-color)",
    backgroundColor: "rgba(67, 95, 134, 0.04)",
  },
  icon: {
    width: 22,
    height: 22,
    flexShrink: 0,
  },
  iconActive: {
    opacity: 1,
  },
  subMenu: {
    overflow: "hidden",
    transition: "max-height 0.3s ease, opacity 0.3s ease",
  },
  subMenuItem: {
    display: "flex",
    alignItems: "center",
    padding: "10px 15px 10px 52px",
    color: "#64748b",
    textDecoration: "none",
    whiteSpace: "nowrap",
    fontSize: "13px",
    fontWeight: 400,
    transition: "all 0.2s ease",
    cursor: "pointer",
    gap: "8px",
  },
  subMenuItemActive: {
    color: "var(--primary-color)",
    fontWeight: 500,
  },
  chevron: {
    marginLeft: "auto",
    transition: "transform 0.3s ease",
    width: 14,
    height: 14,
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#94a3b8",
    padding: "16px 15px 6px",
    letterSpacing: "1.2px",
    whiteSpace: "nowrap",
  },
};

const ChevronDown = ({ isOpen, isRTL, active }) => (
  <svg
    style={{
      ...sidebarStyles.chevron,
      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
      marginLeft: isRTL ? 0 : "auto",
      marginRight: isRTL ? "auto" : 0,
      color: active ? "var(--primary-color)" : "#64748b",
    }}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SubMenuDot = ({ active }) => (
  <span
    style={{
      width: 5,
      height: 5,
      borderRadius: "50%",
      backgroundColor: active ? "var(--primary-color)" : "#cbd5e1",
      flexShrink: 0,
    }}
  />
);

export default function Sidebar({ sideOpen, setSideOpen }) {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const { t, i18n } = useTranslation("layout");
  const { pathname } = useLocation();

  const AuthedUser = JSON.parse(localStorage.getItem("AuthedUser")) || {};
  const { ClientName, DataAnalysisLink } = AuthedUser;

  const isRTL = i18n.dir() === "rtl";

  const renderIcon = (iconPath, active, hoverKey) => {
    const isHovered = hoveredItem === hoverKey;
    const isActive = active || isHovered;

    // Custom SVG icons with new design
    const icons = {
      MenuIcon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="9" rx="1" />
          <rect x="14" y="3" width="7" height="5" rx="1" />
          <rect x="14" y="12" width="7" height="9" rx="1" />
          <rect x="3" y="16" width="7" height="5" rx="1" />
        </svg>
      ),
      ShipmentIcon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
          <polygon points="12 22.08 12 12 3 6.92 3 17.08 12 22.08"></polygon>
          <polygon points="12 12 21 6.92 21 17.08 12 22.08"></polygon>
          <polygon points="12 2 3 6.92 12 12 21 6.92 12 2"></polygon>
          <line x1="12" y1="22.08" x2="12" y2="12"></line>
        </svg>
      ),
      PickupsIcon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="1" x2="12" y2="23"></line>
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
        </svg>
      ),
      ExportDataIcon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      ),
      TicketsIcon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      ),
      AdvancedSearch: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      ),
      NewShipment: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="3" width="15" height="13"></rect>
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
          <circle cx="5.5" cy="18.5" r="2.5"></circle>
          <circle cx="18.5" cy="18.5" r="2.5"></circle>
        </svg>
      ),
      complaint: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      ),
      DataAnalysis: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isActive ? "var(--primary-color)" : "#64748b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
    };

    // Get icon based on the iconPath
    const iconKey = Object.keys(icons).find(key => iconPath.includes(key));
    const IconComponent = iconKey ? icons[iconKey] : null;

    if (IconComponent) {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            flexShrink: 0,
            transition: "all 0.2s ease",
            transform: isActive ? "scale(1.05)" : "scale(1)",
          }}
        >
          {IconComponent}
        </span>
      );
    }

    // Fallback to original method
    return (
      <span
        style={{
          display: "inline-block",
          width: 22,
          height: 22,
          flexShrink: 0,
          backgroundColor:
            active || isHovered ? "var(--primary-color)" : "#64748b",
          maskImage: `url(${iconPath})`,
          WebkitMaskImage: `url(${iconPath})`,
          maskSize: "contain",
          WebkitMaskSize: "contain",
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          maskPosition: "center",
          WebkitMaskPosition: "center",
          transition: "background-color 0.2s ease",
        }}
      />
    );
  };

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (windowWidth < 900) {
    return null;
  }

  const toggleMenu = (key) => {
    setExpandedMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path) => {
    if (path === "/") return pathname === "/";
    return pathname.startsWith(path);
  };

  const isGroupActive = (paths) => {
    return paths.some((p) => pathname.startsWith(p));
  };

  const menuItems = [
    {
      type: "single",
      to: "/",
      icon: MenuIcon,
      label: t("Sidebar.Menu"),
      key: "dashboard",
    },
    {
      type: "group",
      icon: ShipmentIcon,
      label: t("Sidebar.Orders"),
      key: "orders",
      children: [
        { to: "/reports/shipments", label: t("Sidebar.List") },
        { to: "/new-shipment", label: t("Navbar.add_shipment") },
        { to: "/upload", label: t("Navbar.upload_excel") },
      ],
    },
    {
      type: "single",
      to: "/cs-home",
      icon: complaint,
      label: t("Sidebar.Rejected_Orders"),
      key: "rejected",
    },
    {
      type: "group",
      icon: PickupsIcon,
      label: t("Sidebar.Revenue"),
      key: "revenue",
      children: [{ to: "/reports/pickups", label: t("Sidebar.List") }],
    },
    {
      type: "single",
      to: "/new-pickup",
      icon: NewShipment,
      label: t("Sidebar.Pickup_Request"),
      key: "pickup-request",
    },
  ];

  // Add extra items
  const extraItems = [
    {
      to: "/reports/export",
      icon: ExportDataIcon,
      label: t("Sidebar.Reports_ExportData"),
      key: "export",
    },
    {
      to: "/multi-search",
      icon: AdvancedSearch,
      label: "Print AWB",
      key: "search",
    },
    {
      to: "/tickets",
      icon: TicketsIcon,
      label: t("Sidebar.Tickets"),
      key: "tickets",
    },
  ];

  if (DataAnalysisLink && DataAnalysisLink.trim() !== "") {
    extraItems.push({
      to: "/data-analysis",
      icon: DataAnalysis,
      label: t("Sidebar.data_analysis"),
      key: "analysis",
    });
  }

  const getNavItemStyle = (isItemActive, itemKey) => ({
    ...sidebarStyles.navItem,
    ...(isItemActive ? sidebarStyles.navItemActive : {}),
    ...(hoveredItem === itemKey && !isItemActive
      ? sidebarStyles.navItemHover
      : {}),
  });

  const getSubItemStyle = (isItemActive, itemKey) => ({
    ...sidebarStyles.subMenuItem,
    ...(isItemActive ? sidebarStyles.subMenuItemActive : {}),
    ...(hoveredItem === `sub-${itemKey}` && !isItemActive
      ? {
          color: "var(--primary-color)",
          backgroundColor: "rgba(67, 95, 134, 0.04)",
        }
      : {}),
    paddingLeft: isRTL ? 15 : 52,
    paddingRight: isRTL ? 52 : 15,
  });

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      style={{
        ...sidebarStyles.sidebar,
        width: sideOpen ? 220 : 60,
      }}
    >
      {/* User Section */}
      <div
        style={{
          ...sidebarStyles.userSection,
          padding: sideOpen ? "20px 15px" : "20px 5px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {/* Row 1: Profile Avatar, Name, and Toggle Button */}
        <div
          style={{
            display: "flex",
            width: "100%",
            flexDirection: sideOpen ? "row" : "column",
            justifyContent: sideOpen ? "space-between" : "center",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ 
              ...sidebarStyles.avatar, 
              width: sideOpen ? 120 : 42,
              background: "#fff",
              padding: "4px"
            }}>
              <img
                src={logoImg}
                alt="Logo"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain"
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <span
                style={{
                  ...sidebarStyles.avatarFallback,
                  display: "none",
                  width: "100%",
                  height: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {(ClientName || "U").charAt(0).toUpperCase()}
              </span>
            </div>
            {/* {sideOpen && (
              <span style={sidebarStyles.userName}>{ClientName || "User"}</span>
            )} */}
          </div>

          {/* Toggle Button next to name/avatar */}
          <div
            onClick={() => setSideOpen(!sideOpen)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "6px",
              borderRadius: "6px",
              backgroundColor: "rgba(67, 95, 134, 0.08)",
              transition: "all 0.2s ease",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--primary-color)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: sideOpen
                  ? isRTL
                    ? "rotate(180deg)"
                    : "rotate(0deg)"
                  : isRTL
                    ? "rotate(0deg)"
                    : "rotate(180deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </div>
        </div>

        {/* Row 2: Search Input inside userSection (above the divider/borderBottom) */}
        {sideOpen && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (searchText.trim()) {
                navigate(`/shipment/${searchText}`);
                setSearchText("");
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f1f5f9",
              borderRadius: "8px",
              padding: "8px 12px",
              gap: "8px",
              border: "1px solid #cbd5e1",
              width: "100%",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--primary-color)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ cursor: "pointer" }}
              onClick={() => {
                if (searchText.trim()) {
                  navigate(`/shipment/${searchText}`);
                  setSearchText("");
                }
              }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder={t("Navbar.search_placeholder") || "Search..."}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                border: "none",
                background: "transparent",
                outline: "none",
                fontSize: "13px",
                width: "100%",
                color: "#1e293b",
                fontFamily: "inherit",
              }}
            />
          </form>
        )}
      </div>

      {/* Navigation */}
      <nav
        style={{
          ...sidebarStyles.nav,
          msOverflowStyle: "none",
        }}
      >
        {menuItems.map((item) => {
          if (item.type === "single") {
            const active = isActive(item.to);
            return (
              <Link
                key={item.key}
                to={item.to}
                style={getNavItemStyle(active, item.key)}
                onMouseEnter={() => setHoveredItem(item.key)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                {renderIcon(item.icon, active, item.key)}
                {sideOpen && <span>{item.label}</span>}
              </Link>
            );
          }

          // Group items
          const groupActive = isGroupActive(item.children.map((c) => c.to));
          const isExpanded = expandedMenus[item.key] || groupActive;

          return (
            <div key={item.key}>
              <div
                style={getNavItemStyle(groupActive, item.key)}
                onClick={() => sideOpen && toggleMenu(item.key)}
                onMouseEnter={() => setHoveredItem(item.key)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                {renderIcon(item.icon, groupActive, item.key)}
                {sideOpen && (
                  <>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    <ChevronDown
                      isOpen={isExpanded}
                      isRTL={isRTL}
                      active={groupActive}
                    />
                  </>
                )}
              </div>
              {sideOpen && (
                <div
                  style={{
                    ...sidebarStyles.subMenu,
                    maxHeight: isExpanded
                      ? `${item.children.length * 44}px`
                      : "0px",
                    opacity: isExpanded ? 1 : 0,
                  }}
                >
                  {item.children.map((child) => {
                    const childActive = isActive(child.to);
                    return (
                      <Link
                        key={child.to}
                        to={child.to}
                        style={getSubItemStyle(childActive, child.to)}
                        onMouseEnter={() => setHoveredItem(`sub-${child.to}`)}
                        onMouseLeave={() => setHoveredItem(null)}
                      >
                        <SubMenuDot active={childActive} />
                        <span>{child.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Extra items */}
        {extraItems.map((item) => {
          const active = isActive(item.to);
          return (
            <Link
              key={item.key}
              to={item.to}
              style={getNavItemStyle(active, item.key)}
              onMouseEnter={() => setHoveredItem(item.key)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {renderIcon(item.icon, active, item.key)}
              {sideOpen && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
